import { groupBy, isEqualWith, isNil } from 'lodash';
import type { Portfolio } from 'venn-api';
import type { StudioRequestSubject } from 'venn-state';

const PORTFOLIO_TRANSIENT_FIELDS = new Set([
  'updated',
  'compare',
  'startRange',
  'endRange',
  'periodStart',
  'periodEnd',
  'formattedPeriodStartEnd',
]);
export const arePortfoliosEqual = (portfolio: Portfolio, other: Portfolio): boolean =>
  isEqualWith(portfolio, other, (_, __, key: string | number | symbol | undefined) =>
    PORTFOLIO_TRANSIENT_FIELDS.has(String(key)) ? true : undefined,
  );

/**
 * Compares only allocations and tree structure, ignoring edits such as proxies, tax information or cash
 * designation, which don't prevent an aggregated portfolio from being saved as live.
 */
export const areAllocationsAndStructureEqual = (portfolio: Portfolio, other: Portfolio): boolean => {
  if (portfolio.id !== other.id || portfolio.allocation !== other.allocation) {
    return false;
  }
  const children = portfolio.children ?? [];
  const otherChildren = other.children ?? [];
  return (
    children.length === otherChildren.length &&
    children.every((child, index) => areAllocationsAndStructureEqual(child, otherChildren[index]!))
  );
};

export const findInPortfolioBy = <P extends { children?: P[] }>(
  portfolio: P | undefined,
  predicate: (portfolio: P) => boolean,
): P | undefined => {
  for (const node of traversePortfolio(portfolio)) {
    if (predicate(node)) {
      return node;
    }
  }
  return undefined;
};

export const findInPortfolio = (portfolio: Portfolio, id: number): Portfolio | undefined =>
  findInPortfolioBy(portfolio, (portfolio) => portfolio.id === id);

export const showReadOnlyWarning = (portfolio: Portfolio, isModelPortfolio: boolean): boolean => {
  const isLiveAggregatedPortfolio =
    !!portfolio.aggregatedPortfolioSettings && portfolio.aggregatedPortfolioSettings.portfolioInformation?.length;
  return !!(
    portfolio.sourceId ||
    portfolio.remoteId ||
    isModelPortfolio ||
    portfolio.historical ||
    isLiveAggregatedPortfolio
  );
};

export const isPortfolioEditable = (portfolio: Portfolio): boolean => {
  return !(portfolio.sourceId || portfolio.remoteId || portfolio.demo);
};

/**
 * Generator function performing a traversal of a Portfolio's nodes, including the root.
 */
export function* traversePortfolio<P extends { children?: P[] }>(portfolio: P | undefined): IterableIterator<P> {
  if (!portfolio) {
    return;
  }

  const childrenStack: P[][] = [[portfolio]];
  while (childrenStack.length > 0) {
    const children = childrenStack.pop()!;
    for (let i = 0; i < children.length; i++) {
      const child = children[i]!;
      yield child;
      const newChildren = child.children;
      if (newChildren) {
        childrenStack.push(newChildren);
      }
    }
  }
}

type PortfolioLike = {
  allocation?: number;
  children?: PortfolioLike[];
};

export const pruneNodesWithZeroAllocation = <P extends PortfolioLike>(portfolio: P): P => {
  return {
    ...portfolio,
    children: portfolio?.children
      ?.map((child) => pruneNodesWithZeroAllocation(child))
      ?.filter((child) => !!child.allocation),
  };
};

export type PortfolioNode = Pick<Portfolio, 'fund' | 'designation'>;
export type PortfolioNodeWithName = Portfolio & Pick<Portfolio, 'name'>;

export const isFundNode = <T extends PortfolioNode>(node?: T): boolean => {
  return !isNil(node?.fund) && node?.designation !== 'CASH';
};

export const isCashNode = <T extends PortfolioNode>(node?: T): boolean => {
  return node?.designation === 'CASH';
};

export const isStrategyNode = <T extends PortfolioNode>(node?: T): boolean => {
  return !isFundNode(node) && !isCashNode(node);
};

// Option is subtype of a fund node
export const isOptionNode = <T extends PortfolioNode>(node?: T): boolean => {
  return isFundNode(node) && node?.fund?.assetType === 'OPTION';
};

export const getFullName = <T extends PortfolioNodeWithName>(node?: T): string | undefined => {
  if (!node) {
    return undefined;
  }

  const optionSymbol = isOptionNode(node) ? node?.fund?.symbol : undefined;
  return optionSymbol ? `${node.name} (${optionSymbol})` : node.name;
};

export const partitionNodes = <T extends PortfolioNode>(
  nodes?: T[],
): {
  strategies: T[];
  funds: T[];
  cashPositions: T[];
} => {
  const groups = groupBy(nodes ?? [], (node) => {
    if (isFundNode(node)) {
      return 'funds';
    }
    if (isCashNode(node)) {
      return 'cashPositions';
    }
    return 'strategies';
  });

  return {
    strategies: groups.strategies ?? [],
    funds: groups.funds ?? [],
    cashPositions: groups.cashPositions ?? [],
  };
};

export const getPortfolioCashNodes = (portfolio: Portfolio | undefined): Portfolio[] =>
  [...traversePortfolio(portfolio)].filter(isCashNode);

export const getStudioRequestSubjectCashNodes = (subject: StudioRequestSubject | undefined): Portfolio[] =>
  getPortfolioCashNodes(subject?.modifiedPortfolio ?? subject?.portfolio);

export const isPortfolioLabDisabledForProFormaPortfolio = (portfolio: Portfolio | undefined): boolean => {
  return !!findInPortfolioBy(portfolio, (node) => {
    return isCashNode(node) || isOptionNode(node) || (node?.netAssets ?? 0) < 0;
  });
};

export const hasOptions = (portfolio: Portfolio | undefined): boolean => {
  return !!findInPortfolioBy(portfolio, (node) => {
    return isOptionNode(node);
  });
};
